import { z } from "zod";
import { dateOnlySchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { anyEntityTypeSchema, userRefSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { aiProviderSchema } from "./org.ts";

/**
 * "ASK YOUR BUSINESS" — the AI assistant contract (PRD §9 Item 14,
 * docs/product/ai-assistant-spec.md).
 *
 * THE RULE EVERY SHAPE HERE SERVES: the model never computes a number and
 * never writes SQL. Figures reach an answer or a draft as FACT SLOTS — values
 * the server read from the metric registry or a module's public API and
 * rendered itself — and every one of them is returned with its CITATION (what
 * it is, how it is defined, how fresh it is, and which records are behind it).
 *
 * Everything the assistant writes is a DRAFT until a human approves it
 * (`aiDraftSchema.status`); nothing leaves the workspace on the model's say-so.
 */

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

/** Gateway task routes. Each maps to a configured model (or Azure deployment). */
export const AI_ROUTES = ["classify", "answer", "draft", "embedding"] as const;
export type AiRoute = (typeof AI_ROUTES)[number];

export const AI_DRAFT_TYPES = ["reminder", "summary", "weekly_report"] as const;
export type AiDraftType = (typeof AI_DRAFT_TYPES)[number];
export const aiDraftTypeSchema = z.enum(AI_DRAFT_TYPES);

/** draft → approved → published (weekly reports only); any live state → discarded. */
export const AI_DRAFT_STATUSES = ["draft", "approved", "published", "discarded"] as const;
export type AiDraftStatus = (typeof AI_DRAFT_STATUSES)[number];
export const aiDraftStatusSchema = z.enum(AI_DRAFT_STATUSES);

/** How a run of the AI Action Ledger ended. */
export const AI_RUN_OUTCOMES = ["answered", "drafted", "abstained", "refused", "blocked", "error"] as const;
export type AiRunOutcome = (typeof AI_RUN_OUTCOMES)[number];
export const aiRunOutcomeSchema = z.enum(AI_RUN_OUTCOMES);

/** Where the run came from: the Ask bar, a draft button, or an automation (Autopilot). */
export const AI_RUN_SURFACES = ["ask", "draft", "automation"] as const;
export type AiRunSurface = (typeof AI_RUN_SURFACES)[number];
export const aiRunSurfaceSchema = z.enum(AI_RUN_SURFACES);

/** The reminder tone ladder (spec §7.1): heads-up → polite → neutral → firm → final. */
export const AI_REMINDER_TONES = ["gentle", "polite", "neutral", "firm", "final"] as const;
export type AiReminderTone = (typeof AI_REMINDER_TONES)[number];
export const aiReminderToneSchema = z.enum(AI_REMINDER_TONES);

export const AI_LANGUAGES = ["en", "hi", "hinglish"] as const;
export type AiLanguage = (typeof AI_LANGUAGES)[number];
export const aiLanguageSchema = z.enum(AI_LANGUAGES);

export const AI_CHANNELS = ["email", "whatsapp"] as const;
export type AiChannel = (typeof AI_CHANNELS)[number];
export const aiChannelSchema = z.enum(AI_CHANNELS);

/** Client summary lengths (spec §7.2): ≤ 40 words, ≤ 300 words, ≤ 900 words. */
export const AI_SUMMARY_LENGTHS = ["one_liner", "brief", "qbr"] as const;
export type AiSummaryLength = (typeof AI_SUMMARY_LENGTHS)[number];
export const aiSummaryLengthSchema = z.enum(AI_SUMMARY_LENGTHS);

/** Records the Ask bar can be pinned to (the record side panel). */
export const AI_CONTEXT_TYPES = ["client", "invoice", "project", "lead"] as const;
export type AiContextType = (typeof AI_CONTEXT_TYPES)[number];

/** Why the assistant cannot be used right now — the UI shows `message` instead of an error. */
export const AI_UNAVAILABLE_REASONS = ["not_configured", "disabled", "no_permission", "budget_exhausted"] as const;
export type AiUnavailableReason = (typeof AI_UNAVAILABLE_REASONS)[number];

// ---------------------------------------------------------------------------
// Budget and status
// ---------------------------------------------------------------------------

/** The workspace's spend meter for the current calendar month. */
export const aiBudgetStatusSchema = z.object({
  /** First day of the month the meter covers (YYYY-MM-01). */
  month: dateOnlySchema,
  currency: currencyCodeSchema,
  capMinor: minorSchema,
  usedMinor: minorSchema,
  remainingMinor: minorSchema,
  /** used ÷ cap in basis points (10000 = 100%); 10000 when the cap is 0. */
  usedBps: z.number().int(),
  warnAtPct: z.number().int(),
  /** true = runs stop at the cap; false = interactive Ask degrades to the small model. */
  hardStop: z.boolean(),
  state: z.enum(["ok", "warning", "exhausted"]),
});
export type AiBudgetStatusDto = z.infer<typeof aiBudgetStatusSchema>;

/** GET /api/v1/ai/status (perm `ai:use`) — lets the UI say "AI is not configured" instead of failing. */
export const aiAssistantStatusSchema = z.object({
  configured: z.boolean(),
  provider: aiProviderSchema.nullable(),
  /** The workspace's master switch (org AI settings). */
  enabled: z.boolean(),
  /** True when this caller can ask a question right now. */
  canUse: z.boolean(),
  reason: z.enum(AI_UNAVAILABLE_REASONS).nullable(),
  /** A sentence the UI can show as-is, e.g. "AI is not configured for this workspace." */
  message: z.string().nullable(),
  /** Model (or Azure deployment) per route; null when not configured. */
  models: z
    .object({ classify: z.string(), answer: z.string(), draft: z.string(), embedding: z.string() })
    .nullable(),
  allowFinancialData: z.boolean(),
  allowPersonalData: z.boolean(),
  conversationRetentionDays: z.number().int(),
  canSeeFinancials: z.boolean(),
  budget: aiBudgetStatusSchema,
});
export type AiAssistantStatusDto = z.infer<typeof aiAssistantStatusSchema>;

// ---------------------------------------------------------------------------
// Citations ("How I got this")
// ---------------------------------------------------------------------------

/**
 * One receipt. `kind: "metric"` is a figure (key `F1`, `F2`…) with the metric's
 * definition and the period it covers; `kind: "record"` is a source record (key
 * `S1`… in answers, `E1`… in summaries) the text leaned on. The text carries the
 * key in square brackets, e.g. "Outstanding is ₹4,50,000 [F1]", so the UI can turn
 * each into a chip that opens the record or the drill-through.
 */
export const aiCitationSchema = z.object({
  key: z.string(),
  kind: z.enum(["metric", "record"]),
  label: z.string(),
  /** The rendered figure for a metric fact; null for a record citation. */
  display: z.string().nullable(),
  metricId: z.string().nullable(),
  /** The metric's governed definition (from the reports registry). */
  definition: z.string().nullable(),
  /** Human period label, e.g. "September 2026" or "FY 2026-27". */
  period: z.string().nullable(),
  entityType: anyEntityTypeSchema.nullable(),
  entityId: uuidSchema.nullable(),
  /** Web route of the record or the drill-through list. */
  url: z.string().nullable(),
  /** When the underlying data was read. */
  asOf: timestampSchema,
});
export type AiCitationDto = z.infer<typeof aiCitationSchema>;

/** One tool the assistant called while answering — the "How I got this" expander. */
export const aiToolCallSchema = z.object({
  seq: z.number().int(),
  tool: z.string(),
  args: z.record(z.string(), z.unknown()),
  status: z.enum(["ok", "refused", "error"]),
  /** Refusal / error reason, e.g. "You don't have access to financial data." */
  message: z.string().nullable(),
  recordCount: z.number().int(),
  durationMs: z.number().int(),
});
export type AiToolCallDto = z.infer<typeof aiToolCallSchema>;

// ---------------------------------------------------------------------------
// Threads and messages
// ---------------------------------------------------------------------------

export const aiContextSchema = z.object({ entityType: z.enum(AI_CONTEXT_TYPES), entityId: uuidSchema });
export type AiContextDto = z.infer<typeof aiContextSchema>;

export const aiMessageSchema = z.object({
  id: uuidSchema,
  threadId: uuidSchema,
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  citations: z.array(aiCitationSchema),
  toolCalls: z.array(aiToolCallSchema),
  outcome: aiRunOutcomeSchema.nullable(),
  runId: uuidSchema.nullable(),
  /** e.g. "2 invoices in USD are not included in a rupee total." */
  caveats: z.array(z.string()),
  /** Follow-up questions (at most 3). */
  suggestions: z.array(z.string()),
  /** Always "AI-generated" on assistant messages; null on the user's own. */
  label: z.string().nullable(),
  createdAt: timestampSchema,
});
export type AiMessageDto = z.infer<typeof aiMessageSchema>;

export const aiThreadSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  context: aiContextSchema.nullable(),
  messageCount: z.number().int(),
  lastMessageAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type AiThreadDto = z.infer<typeof aiThreadSchema>;

/** GET /api/v1/ai/threads/:id (perm `ai:use`, own threads only) → thread + messages, oldest first. */
export const aiThreadDetailSchema = aiThreadSchema.extend({ messages: z.array(aiMessageSchema) });
export type AiThreadDetailDto = z.infer<typeof aiThreadDetailSchema>;

/** GET /api/v1/ai/threads (perm `ai:use`) — the caller's own conversations, newest first. */
export const listAiThreadsQuerySchema = listQuerySchema;
export type ListAiThreadsQuery = z.infer<typeof listAiThreadsQuerySchema>;

/** POST /api/v1/ai/threads (perm `ai:use`) → 201 Thread. */
export const createAiThreadInputSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    context: aiContextSchema.strict().optional(),
  })
  .strict();
export type CreateAiThreadInput = z.input<typeof createAiThreadInputSchema>;

/** PATCH /api/v1/ai/threads/:id (perm `ai:use`, own thread) → Thread. */
export const updateAiThreadInputSchema = z.object({ title: z.string().trim().min(1).max(120) }).strict();
export type UpdateAiThreadInput = z.input<typeof updateAiThreadInputSchema>;

// ---------------------------------------------------------------------------
// Ask
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/ai/ask (perm `ai:use`). Streams Server-Sent Events by default
 * (`AiAskEvent` below); `stream: false` returns `aiAskResponseSchema` as JSON.
 * Not configured → 503 INTEGRATION_NOT_CONFIGURED; budget exhausted → 429.
 */
export const aiAskInputSchema = z
  .object({
    question: z.string().trim().min(1).max(2000),
    threadId: uuidSchema.optional(),
    /** Pins a record as context (the record side panel). */
    context: aiContextSchema.strict().optional(),
    stream: z.boolean().optional(),
  })
  .strict();
export type AiAskInput = z.input<typeof aiAskInputSchema>;

export const aiAskResponseSchema = z.object({
  threadId: uuidSchema,
  runId: uuidSchema,
  question: aiMessageSchema,
  answer: aiMessageSchema,
});
export type AiAskResponseDto = z.infer<typeof aiAskResponseSchema>;

/** The SSE event names POST /ai/ask emits, in order. */
export const AI_ASK_EVENT_TYPES = [
  "run.started",
  "tool.started",
  "tool.completed",
  "answer.delta",
  "answer.reset",
  "answer.completed",
  "error",
  "done",
] as const;

export const aiAskEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("run.started"), runId: uuidSchema, threadId: uuidSchema, questionId: uuidSchema }),
  z.object({ type: z.literal("tool.started"), seq: z.number().int(), tool: z.string(), label: z.string() }),
  z.object({ type: z.literal("tool.completed"), call: aiToolCallSchema }),
  /** Best-effort streamed text; the authoritative text is `answer.completed`. */
  z.object({ type: z.literal("answer.delta"), text: z.string() }),
  /** Discard the streamed text so far (the model went on to call a tool, or the answer is being rewritten). */
  z.object({ type: z.literal("answer.reset") }),
  z.object({ type: z.literal("answer.completed"), message: aiMessageSchema }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
  z.object({ type: z.literal("done") }),
]);
export type AiAskEvent = z.infer<typeof aiAskEventSchema>;

// ---------------------------------------------------------------------------
// Drafts (J1 reminder, J2 client summary, J3 weekly client report)
// ---------------------------------------------------------------------------

/**
 * A LOCKED FACT. The draft's text carries `{{key}}`; the server renders
 * `display` in its place from structured data, so the model can never type a
 * wrong amount, date, invoice number or payment link. The editor shows slots as
 * chips a person may delete but not alter.
 */
export const aiFactSlotSchema = z.object({
  key: z.string(),
  label: z.string(),
  display: z.string(),
  kind: z.enum(["money", "date", "text", "link", "number", "percent"]),
  entityType: anyEntityTypeSchema.nullable(),
  entityId: uuidSchema.nullable(),
});
export type AiFactSlotDto = z.infer<typeof aiFactSlotSchema>;

/** A literal the validator refused (an amount, a date, a document number, a URL…) — removed from the text. */
export const aiViolationSchema = z.object({
  field: z.string(),
  text: z.string(),
  kind: z.enum(["amount", "percent", "number", "date", "document_number", "url", "gstin", "account_number", "upi", "unknown_slot", "promise", "length", "uncited"]),
});
export type AiViolationDto = z.infer<typeof aiViolationSchema>;

/** One line of a weekly report section — always a client-visible record, never an internal one. */
export const aiDraftItemSchema = z.object({
  label: z.string(),
  detail: z.string().nullable(),
  status: z.string().nullable(),
  entityType: anyEntityTypeSchema.nullable(),
  entityId: uuidSchema.nullable(),
  url: z.string().nullable(),
});
export type AiDraftItemDto = z.infer<typeof aiDraftItemSchema>;

export const aiDraftSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  /** Rendered text (slots filled). */
  body: z.string(),
  /** Editable text with `{{slot}}` tokens and `[E1]` citation markers. */
  bodyTemplate: z.string(),
  items: z.array(aiDraftItemSchema),
});
export type AiDraftSectionDto = z.infer<typeof aiDraftSectionSchema>;

export const aiDraftPublicationSchema = z.object({
  snapshotId: uuidSchema,
  version: z.number().int(),
  publishedAt: timestampSchema,
  /** First time a client contact opened it in the portal (read receipt). */
  firstViewedAt: timestampSchema.nullable(),
  viewCount: z.number().int(),
});
export type AiDraftPublicationDto = z.infer<typeof aiDraftPublicationSchema>;

export const aiDraftSchema = z.object({
  id: uuidSchema,
  type: aiDraftTypeSchema,
  status: aiDraftStatusSchema,
  title: z.string(),
  /** The record the draft is about: the invoice (a reminder) or the client. */
  subjectType: z.enum(["invoice", "client"]),
  subjectId: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  language: aiLanguageSchema,
  channel: aiChannelSchema.nullable(),
  tone: aiReminderToneSchema.nullable(),
  length: aiSummaryLengthSchema.nullable(),
  /** Monday of the reported week (weekly reports). */
  weekStart: dateOnlySchema.nullable(),
  /** Email subject / report title, rendered. */
  subject: z.string().nullable(),
  subjectTemplate: z.string().nullable(),
  /** The message (reminders) or the headline (reports, summaries), rendered. */
  body: z.string(),
  bodyTemplate: z.string(),
  sections: z.array(aiDraftSectionSchema),
  /** The account manager's own notes (weekly report section 8). Free text, not validated. */
  notes: z.string().nullable(),
  slots: z.array(aiFactSlotSchema),
  citations: z.array(aiCitationSchema),
  violations: z.array(aiViolationSchema),
  /** Why the draft looks the way it does, e.g. "14 days overdue → firm tone". */
  reasons: z.array(z.string()),
  /** "AI draft · not reviewed yet" / "Prepared with AI · Reviewed by Priya S." */
  label: z.string(),
  edited: z.boolean(),
  /** True when the facts moved since drafting (e.g. the invoice was paid) — re-check before approving. */
  stale: z.boolean(),
  runId: uuidSchema.nullable(),
  model: z.string().nullable(),
  createdBy: userRefSchema.nullable(),
  reviewer: userRefSchema.nullable(),
  approvedBy: userRefSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  publishedBy: userRefSchema.nullable(),
  publishedAt: timestampSchema.nullable(),
  publication: aiDraftPublicationSchema.nullable(),
  discardedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type AiDraftDto = z.infer<typeof aiDraftSchema>;

/**
 * POST /api/v1/ai/drafts/reminder (perms `ai:use`, `invoices:read`, `financials:view`) → 201 Draft.
 * Exactly one of `invoiceId` / `clientId` (a client → one message for all its overdue invoices).
 * Nothing outstanding → CONFLICT.
 */
export const createAiReminderDraftInputSchema = z
  .object({
    invoiceId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    /** "auto" (default) picks the tone from the ladder and the client's payment history. */
    tone: z.enum(["auto", ...AI_REMINDER_TONES]).optional(),
    language: aiLanguageSchema.optional(),
    channel: aiChannelSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (Boolean(value.invoiceId) === Boolean(value.clientId)) {
      ctx.addIssue({ code: "custom", path: ["invoiceId"], message: "Pick an invoice or a client (exactly one)." });
    }
  });
export type CreateAiReminderDraftInput = z.input<typeof createAiReminderDraftInputSchema>;

/** POST /api/v1/ai/drafts/client-summary (perms `ai:use`, `clients:read`) → 201 Draft. */
export const createAiClientSummaryInputSchema = z
  .object({ clientId: uuidSchema, length: aiSummaryLengthSchema.optional(), language: aiLanguageSchema.optional() })
  .strict();
export type CreateAiClientSummaryInput = z.input<typeof createAiClientSummaryInputSchema>;

/**
 * POST /api/v1/ai/drafts/weekly-report (perms `ai:use`, `clients:read`, `projects:read`) → 201 Draft.
 * One live draft per client per week: a repeat returns the existing one unless `regenerate`.
 */
export const createAiWeeklyReportInputSchema = z
  .object({
    clientId: uuidSchema,
    /** Any day inside the week to report on (Monday–Sunday, workspace timezone). Defaults to this week. */
    weekOf: dateOnlySchema.optional(),
    regenerate: z.boolean().optional(),
  })
  .strict();
export type CreateAiWeeklyReportInput = z.input<typeof createAiWeeklyReportInputSchema>;

/**
 * PATCH /api/v1/ai/drafts/:id (perm `ai:use` + the draft type's read perms) → Draft.
 * Merge patch. Slot tokens must be ones the draft already has; typed amounts, document
 * numbers, links, GSTINs and account numbers are refused (VALIDATION_FAILED) — use the chips.
 * Sections are matched by `key`; a section omitted is unchanged.
 */
export const updateAiDraftInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    subject: z.string().trim().max(200).nullable(),
    body: z.string().max(20_000),
    sections: z.array(z.object({ key: z.string().min(1).max(40), body: z.string().max(20_000) }).strict()).max(20),
    notes: z.string().max(10_000).nullable(),
  })
  .partial()
  .strict();
export type UpdateAiDraftInput = z.input<typeof updateAiDraftInputSchema>;

/** POST /api/v1/ai/drafts/:id/discard → Draft. */
export const discardAiDraftInputSchema = z.object({ reason: z.string().trim().max(500).optional() }).strict();
export type DiscardAiDraftInput = z.input<typeof discardAiDraftInputSchema>;

/**
 * POST /api/v1/ai/drafts/:id/publish (perms `ai:use`, `portal:manage`; weekly reports only) → Draft.
 * Writes an immutable portal snapshot (a new version when republished after an edit) and
 * notifies the client's portal contacts. A repeat with no change publishes nothing new.
 */
export const publishAiDraftInputSchema = z.object({ notify: z.boolean().optional() }).strict();
export type PublishAiDraftInput = z.input<typeof publishAiDraftInputSchema>;

/** GET /api/v1/ai/drafts (perm `ai:use`) — the Review Inbox. */
export const listAiDraftsQuerySchema = listQuerySchema.extend({
  type: aiDraftTypeSchema.optional(),
  status: aiDraftStatusSchema.optional(),
  clientId: uuidSchema.optional(),
  subjectId: uuidSchema.optional(),
});
export type ListAiDraftsQuery = z.infer<typeof listAiDraftsQuerySchema>;

// ---------------------------------------------------------------------------
// Usage, budget, ledger, feedback
// ---------------------------------------------------------------------------

/** GET /api/v1/ai/usage (perm `ai:use`) — the admin cost meter. */
export const aiUsageSchema = aiBudgetStatusSchema.extend({
  runs: z.number().int(),
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  byFeature: z.array(z.object({ feature: z.string(), runs: z.number().int(), costMinor: minorSchema })),
  byUser: z.array(z.object({ user: userRefSchema.nullable(), runs: z.number().int(), costMinor: minorSchema })),
  history: z.array(z.object({ month: dateOnlySchema, runs: z.number().int(), costMinor: minorSchema })),
});
export type AiUsageDto = z.infer<typeof aiUsageSchema>;

/** PUT /api/v1/ai/budget (perm `org:manage`) → Usage. Merge patch. */
export const updateAiBudgetInputSchema = z
  .object({
    monthlyCapMinor: minorSchema.min(0).max(1_000_000_000),
    hardStop: z.boolean(),
    warnAtPct: z.number().int().min(1).max(100),
  })
  .partial()
  .strict();
export type UpdateAiBudgetInput = z.input<typeof updateAiBudgetInputSchema>;

/** One row of the AI Action Ledger (append-only). Prompts and outputs are NOT here — only metadata. */
export const aiRunSchema = z.object({
  id: uuidSchema,
  user: userRefSchema.nullable(),
  surface: aiRunSurfaceSchema,
  intent: z.string(),
  promptVersion: z.string(),
  modelRoute: z.string(),
  model: z.string().nullable(),
  provider: z.string().nullable(),
  tainted: z.boolean(),
  injectionSuspected: z.boolean(),
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  llmCalls: z.number().int(),
  costMinor: minorSchema,
  costCurrency: z.string(),
  latencyMs: z.number().int(),
  outcome: aiRunOutcomeSchema,
  errorCode: z.string().nullable(),
  toolCalls: z.array(aiToolCallSchema),
  recordCount: z.number().int(),
  threadId: uuidSchema.nullable(),
  draftId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type AiRunDto = z.infer<typeof aiRunSchema>;

/** GET /api/v1/ai/runs (perm `audit:view`) — the ledger, newest first. */
export const listAiRunsQuerySchema = listQuerySchema.extend({
  userId: uuidSchema.optional(),
  outcome: aiRunOutcomeSchema.optional(),
  surface: aiRunSurfaceSchema.optional(),
});
export type ListAiRunsQuery = z.infer<typeof listAiRunsQuerySchema>;

/** POST /api/v1/ai/feedback (perm `ai:use`) → 201 Feedback. One vote per person per target (a repeat replaces it). */
export const createAiFeedbackInputSchema = z
  .object({
    runId: uuidSchema.optional(),
    messageId: uuidSchema.optional(),
    draftId: uuidSchema.optional(),
    rating: z.enum(["up", "down"]),
    note: z.string().trim().max(2000).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const targets = [value.runId, value.messageId, value.draftId].filter(Boolean).length;
    if (targets !== 1) ctx.addIssue({ code: "custom", path: ["messageId"], message: "Rate exactly one answer, draft or run." });
  });
export type CreateAiFeedbackInput = z.input<typeof createAiFeedbackInputSchema>;

export const aiFeedbackSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema.nullable(),
  messageId: uuidSchema.nullable(),
  draftId: uuidSchema.nullable(),
  rating: z.enum(["up", "down"]),
  note: z.string().nullable(),
  createdAt: timestampSchema,
});
export type AiFeedbackDto = z.infer<typeof aiFeedbackSchema>;

// ---------------------------------------------------------------------------
// Client portal — published weekly reports (US-14.4 meets Item 9)
// ---------------------------------------------------------------------------

/** GET /api/portal/v1/reports (portal capability `projects:view`) — newest first, latest version of each. */
export const portalWeeklyReportSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  weekStart: dateOnlySchema,
  version: z.number().int(),
  publishedAt: timestampSchema,
  /** "Prepared with AI · Reviewed by Priya S." */
  label: z.string(),
  /** "Updated on 21/09/2026" for a corrected version, else null. */
  updatedNote: z.string().nullable(),
});
export type PortalWeeklyReportSummaryDto = z.infer<typeof portalWeeklyReportSummarySchema>;

/** GET /api/portal/v1/reports/:id — one published report (records a read receipt). */
export const portalWeeklyReportSchema = portalWeeklyReportSummarySchema.extend({
  headline: z.string(),
  sections: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      body: z.string(),
      items: z.array(z.object({ label: z.string(), detail: z.string().nullable(), status: z.string().nullable() })),
    }),
  ),
  notes: z.string().nullable(),
});
export type PortalWeeklyReportDto = z.infer<typeof portalWeeklyReportSchema>;
